import React, { useState } from 'react';
import { useStore } from '../store';
import { BarChart2, AlertCircle, Loader, CheckCircle2, XCircle } from 'lucide-react';
import { DataQualityAnalyzer } from '../utils/dataQuality';
import { QualityReport } from './QualityReport';

export const Analysis: React.FC = () => {
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qualityReport, setQualityReport] = useState<any>(null);
  const [qualityConfig, setQualityConfig] = useState<any>(null);
  
  const { 
    fileData,
    workshopColumn,
    axes,
    scaleMapping,
    setAnalysisResults,
    setCurrentPage,
    canProceedToAnalysis
  } = useStore();

  const getValidationStatus = () => {
    const status = {
      fileData: Boolean(fileData?.length),
      workshopColumn: Boolean(workshopColumn),
      axes: Boolean(Object.keys(axes || {}).length),
      scaleMapping: Boolean(Object.keys(scaleMapping || {}).length)
    };
    
    return status;
  };

  const handleAnalysis = async () => {
    try {
      setAnalyzing(true);
      setError(null);

      // تحليل جودة البيانات أولاً
      if (fileData && fileData.length > 0) {
        const qualityAnalyzer = new DataQualityAnalyzer(fileData);
        const { cleanedData, report } = qualityAnalyzer.cleanData(fileData);
        const config = qualityAnalyzer.getConfig();
        
        setQualityReport(report);
        setQualityConfig(config);
        
        // استخدام البيانات المنظفة للتحليل
        const dataToUse = cleanedData.length > 0 ? cleanedData : fileData;
        
        console.log('Quality Report:', report);
        console.log('Using cleaned data:', dataToUse.length, 'rows out of', fileData.length);
      }

      // Calculate results for each axis
      const results = {
        overall: { average: 0, percentage: 0, performance_level: '' },
        by_axis: {} as Record<string, any>,
        by_workshop: {} as Record<string, any>
      };

      // Group data by workshop
      const workshopGroups = fileData.reduce((acc, row) => {
        const workshop = row[workshopColumn];
        if (!acc[workshop]) acc[workshop] = [];
        acc[workshop].push(row);
        return acc;
      }, {} as Record<string, any[]>);

      // Process each workshop
      for (const [workshop, rows] of Object.entries(workshopGroups)) {
        const workshopResults = {
          overall_average: 0,
          percentage: 0,
          by_axis: {} as Record<string, any>,
          performance_level: ''
        };

        // Process each axis
        let totalAxisAverage = 0;
        let axisCount = 0;

        for (const [axisName, questions] of Object.entries(axes)) {
          const axisValues: number[] = [];
          const questionAverages: Record<string, number> = {};
          const questionPercentages: Record<string, number> = {};

          // Process each question
          for (const question of questions) {
            const values = rows
              .map(row => {
                const response = row[question];
                if (typeof response === 'number') return response;
                if (typeof response === 'string') {
                  const numericValue = scaleMapping[response.trim()];
                  return typeof numericValue === 'number' ? numericValue : null;
                }
                return null;
              })
              .filter((v): v is number => v !== null && !isNaN(v));

            if (values.length > 0) {
              const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
              questionAverages[question] = avg;
              questionPercentages[question] = (avg / 5) * 100; // Convert to percentage
              axisValues.push(avg);
            }
          }

          if (axisValues.length > 0) {
            const axisAverage = axisValues.reduce((sum, val) => sum + val, 0) / axisValues.length;
            const axisPercentage = (axisAverage / 5) * 100;
            totalAxisAverage += axisAverage;
            axisCount++;

            workshopResults.by_axis[axisName] = {
              average: axisAverage,
              percentage: axisPercentage,
              question_averages: questionAverages,
              question_percentages: questionPercentages,
              performance_level: getPerformanceLevel(axisAverage)
            };
          }
        }

        // Calculate workshop overall average
        if (axisCount > 0) {
          workshopResults.overall_average = totalAxisAverage / axisCount;
          workshopResults.percentage = (workshopResults.overall_average / 5) * 100;
          workshopResults.performance_level = getPerformanceLevel(workshopResults.overall_average);
        }

        results.by_workshop[workshop] = workshopResults;
      }

      // Calculate overall axis averages
      let overallTotal = 0;
      let overallCount = 0;

      for (const [axisName, questions] of Object.entries(axes)) {
        const axisData = {
          average: 0,
          percentage: 0,
          question_averages: {} as Record<string, number>,
          question_percentages: {} as Record<string, number>,
          performance_level: ''
        };

        const axisValues: number[] = [];

        // Process each question across all workshops
        for (const question of questions) {
          const values = fileData
            .map(row => {
              const response = row[question];
              if (typeof response === 'number') return response;
              if (typeof response === 'string') {
                const numericValue = scaleMapping[response.trim()];
                return typeof numericValue === 'number' ? numericValue : null;
              }
              return null;
            })
            .filter((v): v is number => v !== null && !isNaN(v));

          if (values.length > 0) {
            const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
            axisData.question_averages[question] = avg;
            axisData.question_percentages[question] = (avg / 5) * 100;
            axisValues.push(avg);
          }
        }

        if (axisValues.length > 0) {
          const axisAverage = axisValues.reduce((sum, val) => sum + val, 0) / axisValues.length;
          axisData.average = axisAverage;
          axisData.percentage = (axisAverage / 5) * 100;
          axisData.performance_level = getPerformanceLevel(axisAverage);
          
          overallTotal += axisAverage;
          overallCount++;
        }

        results.by_axis[axisName] = axisData;
      }

      // Calculate overall average and percentage
      if (overallCount > 0) {
        results.overall.average = overallTotal / overallCount;
        results.overall.percentage = (results.overall.average / 5) * 100;
        results.overall.performance_level = getPerformanceLevel(results.overall.average);
      }

      // Update store with results
      setAnalysisResults(results);
      
      // Navigate to dashboard
      setCurrentPage('dashboard');

    } catch (error) {
      console.error('Analysis error:', error);
      setError(error instanceof Error ? error.message : 'حدث خطأ أثناء تحليل البيانات');
    } finally {
      setAnalyzing(false);
    }
  };

  const getPerformanceLevel = (average: number): string => {
    if (average >= 4.5) return 'ممتاز';
    if (average >= 4.0) return 'جيد جدا';
    if (average >= 3.5) return 'جيد';
    if (average >= 3.0) return 'مقبول';
    return 'ضعيف';
  };

  const validationStatus = getValidationStatus();
  const canProceed = canProceedToAnalysis();

  return (
    <div className="p-6" dir="rtl">
      <h2 className="text-2xl font-bold mb-6 text-right">تحليل البيانات 📊</h2>

      {/* عرض تقرير جودة البيانات */}
      {qualityReport && qualityConfig && (
        <QualityReport 
          report={qualityReport} 
          config={qualityConfig} 
        />
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <BarChart2 className="w-8 h-8 text-blue-500" />
            <h3 className="text-xl font-semibold">جاهز للتحليل</h3>
          </div>

          {/* Prerequisites Status */}
          <div className="mb-6 bg-gray-50 p-4 rounded-lg max-w-md mx-auto">
            <h4 className="font-semibold mb-3 text-right">الخطوات المطلوبة:</h4>
            <ul className="space-y-2 text-right">
              <li className="flex items-center justify-end gap-2">
                {validationStatus.fileData ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <span>تحميل ملف البيانات</span>
              </li>
              <li className="flex items-center justify-end gap-2">
                {validationStatus.workshopColumn ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <span>تحديد عمود ورش العمل</span>
              </li>
              <li className="flex items-center justify-end gap-2">
                {validationStatus.axes ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <span>تكوين المحاور</span>
              </li>
              <li className="flex items-center justify-end gap-2">
                {validationStatus.scaleMapping ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <span>إعداد مقياس التقييم</span>
              </li>
            </ul>
          </div>

          <button
            onClick={handleAnalysis}
            disabled={analyzing || !canProceed}
            className={`
              bg-blue-500 text-white px-8 py-3 rounded-lg transition-colors
              flex items-center gap-2 mx-auto
              ${(analyzing || !canProceed) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'}
            `}
          >
            {analyzing ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>جاري التحليل...</span>
              </>
            ) : (
              <>
                <BarChart2 className="w-5 h-5" />
                <span>تحليل البيانات</span>
              </>
            )}
          </button>

          {!canProceed && !analyzing && (
            <div className="mt-4 p-3 bg-yellow-50 text-yellow-700 rounded-lg flex items-center gap-2 justify-end">
              <span className="text-right">يرجى إكمال جميع الخطوات المطلوبة قبل البدء في التحليل</span>
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg flex items-center gap-2 justify-end">
              <span className="text-right">{error}</span>
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Analysis Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2 text-right">ما سيتم حسابه:</h4>
            <ul className="list-disc list-inside text-right text-gray-600 space-y-1">
              <li>تحليل ذكي لجودة البيانات ومعالجة تلقائية</li>
              <li>متوسط كل سؤال على حدة</li>
              <li>المتوسط العام لكل محور</li>
              <li>المتوسط العام لكل ورشة عمل</li>
              <li>المتوسط العام للاستبيان ككل</li>
              <li>النسبة المئوية لكل متوسط</li>
              <li>توصيات لتحسين جودة الاستبيان</li>
            </ul>
          </div>

          {/* Data Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2 text-right">ملخص البيانات:</h4>
            <ul className="list-disc list-inside text-right text-gray-600 space-y-1">
              <li>{Object.keys(axes).length} محاور للتحليل</li>
              <li>{Object.values(axes).flat().length} سؤال إجمالي</li>
              <li>
                {fileData ? new Set(fileData.map(row => row[workshopColumn || ''])).size : 0} ورشة عمل
              </li>
              <li>{fileData?.length || 0} استجابة</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="mt-8">
        <button
          onClick={() => setCurrentPage('scale_config')}
          disabled={analyzing}
          className={`
            px-6 py-2 rounded-lg text-gray-600 hover:bg-gray-100
            ${analyzing ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          العودة إلى إعداد المقياس
        </button>
      </div>
    </div>
  );
};